import { useEffect, useState } from "react";
import { Button, Modal, Spinner, Table } from "react-bootstrap";
import moment from "moment";
import Swal from "sweetalert2";
import { BsTrash } from "react-icons/bs";
import { useFetch } from "../../hooks/useFetch";
import { listDocumentHistoryService } from "./services/listDocumentHistory.service";
import { deleteDocumentHistoryService } from "./services/deleteDocumentHistory.service";

const actionBtnStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: "0.35rem",
  borderRadius: "6px",
  fontWeight: 500,
  fontSize: "0.8rem",
  padding: "0.25rem 0.65rem",
};

const FTP_PREFIX_BY_TYPE = {
  institucional: "pdfsInstitucional",
  safeCars: "pdfsVehiculosSeguros",
};

function ModalDocumentHistory({
  isOpen,
  onClose,
  documentId,
  documentLabel,
  isAdmin,
  historyType = "institucional",
}) {
  const fetchData = useFetch();
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const HOST_FTP = (process.env.REACT_APP_HOST_FTP || "").replace(/\/$/, "");
  const ftpFolder = FTP_PREFIX_BY_TYPE[historyType] || FTP_PREFIX_BY_TYPE.institucional;

  const loadHistory = async () => {
    try {
      setLoading(true);
      const data = await listDocumentHistoryService(fetchData)(
        documentId,
        historyType
      );
      setVersions(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error al cargar historial de documentos", error);
      setVersions([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isOpen || !documentId) return;
    loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, documentId, historyType]);

  const handleUrlFile = (nameArchivo) =>
    `${HOST_FTP}/${ftpFolder}/${nameArchivo}`;

  const handleDelete = async (version) => {
    const result = await Swal.fire({
      icon: "warning",
      title: "Eliminar versión",
      text: "¿Seguro que deseas eliminar esta versión del historial? Esta acción no se puede deshacer.",
      showCancelButton: true,
      confirmButtonText: "Eliminar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#dc3545",
    });

    if (!result.isConfirmed) return;

    try {
      setDeletingId(version.id_historial);
      await deleteDocumentHistoryService(fetchData)(
        version.id_historial,
        historyType
      );
      setVersions((prev) =>
        prev.filter((item) => item.id_historial !== version.id_historial)
      );
      Swal.fire({
        icon: "success",
        title: "Versión eliminada",
        text: "La versión se eliminó del historial correctamente",
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.message,
      });
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <Modal show={isOpen} onHide={onClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          Historial{documentLabel ? `: ${documentLabel}` : ""}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <div className="text-center py-4">
            <Spinner animation="border" />
          </div>
        ) : versions.length === 0 ? (
          <p className="mb-0 text-muted">No hay versiones anteriores.</p>
        ) : (
          <>
            <p className="text-muted small mb-2">
              Máximo 5 versiones por documento (incluyendo la vigente). Al
              alcanzar el límite no se podrán subir más archivos.
            </p>
            <Table striped bordered hover responsive size="sm">
              <thead>
                <tr>
                  <th>Fecha archivado</th>
                  <th>Archivo</th>
                  <th>Usuario</th>
                  <th style={{ width: "1%" }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {versions.map((version) => (
                  <tr key={version.id_historial}>
                    <td>
                      {version.fecha_archivado
                        ? moment(version.fecha_archivado).format(
                            "DD/MM/YYYY HH:mm"
                          )
                        : "-"}
                    </td>
                    <td>{version.name_archivo}</td>
                    <td>{version.nombre_usuario || "-"}</td>
                    <td>
                      <div className="d-flex align-items-center gap-2 flex-nowrap">
                        <Button
                          as="a"
                          href={handleUrlFile(version.name_archivo)}
                          target="_blank"
                          rel="noreferrer"
                          variant="outline-primary"
                          size="sm"
                          style={actionBtnStyle}
                        >
                          Ver PDF
                        </Button>
                        {isAdmin && (
                          <Button
                            variant="outline-danger"
                            size="sm"
                            style={actionBtnStyle}
                            disabled={deletingId === version.id_historial}
                            onClick={() => handleDelete(version)}
                          >
                            {deletingId === version.id_historial ? (
                              <Spinner animation="border" size="sm" />
                            ) : (
                              <>
                                <BsTrash size={14} />
                                Eliminar
                              </>
                            )}
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </>
        )}
      </Modal.Body>
    </Modal>
  );
}

export default ModalDocumentHistory;
